import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "@/components/brand/Logo";
import MobileMenu from "@/components/site/MobileMenu";
import { whatsappLink, COMPANY, WHATSAPP_DEFAULT_MSG } from "@/data/constants";

// Menu principal padronizado em todas as páginas. "Home" é adicionado
// dinamicamente no início da lista, exceto na própria home (ver navItems).
const NAV = [
  { href: "/catalogo", label: "Catálogo", route: true },
  { href: "/blog", label: "Blog", route: true },
  { href: "#contato", label: "Contato" },
];
const HOME_ITEM = { href: "#home", label: "Home" };

const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return scrolled;
};

const resolveHref = (item, pathname) => {
  if (item.route) return item.href;
  return pathname !== "/" ? "/" + item.href : item.href;
};

export const Header = ({ onQuoteClick }) => {
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const navItems = isHome ? NAV : [HOME_ITEM, ...NAV];

  const handleNav = (e, item) => {
    if (item.route) return;
    if (!item.href.startsWith("#")) return;
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/" + item.href);
    } else {
      const el = document.querySelector(item.href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleMobileNav = (e, item) => {
    handleNav(e, item);
    setOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`mn-header fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 ${
        scrolled ? "shadow-[0_1px_0_rgba(0,0,0,0.05)]" : ""
      }`}
    >
      {/* Linha superior — contato + selo institucional. Some em telas menores
          que lg (o WhatsappFloat cobre o contato rápido no mobile) e também
          recolhe ao rolar a página, deixando só a linha principal fixa. */}
      <div
        className={`hidden lg:block overflow-hidden bg-[var(--mn-cream)] border-b transition-[max-height,opacity,border-color] duration-300 ease-in-out ${
          scrolled
            ? "max-h-0 opacity-0 border-transparent"
            : "max-h-9 opacity-100 border-[var(--mn-line)]"
        }`}
        data-testid="header-topbar"
      >
        <div className="mn-container flex items-center justify-between h-9 text-[0.72rem]">
          <div className="flex items-baseline gap-2" data-testid="header-heritage-tag">
            <span className="uppercase tracking-[0.2em] font-semibold text-[var(--mn-gold)]">
              Grupo ROSE S Sacolas
            </span>
            <span className="text-[var(--mn-dark)]/70">+20 anos de tradição</span>
          </div>
          <div className="flex items-center gap-5 font-medium tracking-wide">
            <a
              href={`https://wa.me/${COMPANY.phonesRaw[0]}?text=${WHATSAPP_DEFAULT_MSG}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--mn-dark)] hover:text-[var(--mn-gold)] transition-colors"
              data-testid="header-whatsapp-link"
            >
              <i className="fa-brands fa-whatsapp mr-1.5 text-[var(--mn-gold)]" />
              {COMPANY.phonesDisplay[0]}
            </a>
            <a
              href={`https://wa.me/${COMPANY.phonesRaw[1]}?text=${WHATSAPP_DEFAULT_MSG}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--mn-dark)] hover:text-[var(--mn-gold)] transition-colors"
              data-testid="header-whatsapp-link-2"
            >
              <i className="fa-brands fa-whatsapp mr-1.5 text-[var(--mn-gold)]" />
              {COMPANY.phonesDisplay[1]}
            </a>
            <a
              href={`tel:+${COMPANY.landlineRaw}`}
              className="text-[var(--mn-dark)] hover:text-[var(--mn-gold)] transition-colors"
              data-testid="header-landline-link"
            >
              <i className="fa-solid fa-phone mr-1.5 text-[var(--mn-gold)]" />
              {COMPANY.landlineDisplay}
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              className="text-[var(--mn-dark)] hover:text-[var(--mn-gold)] transition-colors"
              data-testid="header-email-link"
            >
              <i className="fa-regular fa-envelope mr-1.5 text-[var(--mn-gold)]" />
              {COMPANY.email}
            </a>
          </div>
        </div>
      </div>

      {/* Linha principal — logo, navegação e CTA */}
      <div className="mn-container flex items-center justify-between h-[104px]">
        <Logo size="xl" variant="gold" />

        <nav className="hidden lg:flex items-center gap-9" aria-label="Navegação principal">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={resolveHref(item, location.pathname)}
              onClick={(e) => handleNav(e, item)}
              className="mn-nav-link"
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:block">
          <button
            type="button"
            onClick={onQuoteClick}
            className="btn-mn-primary !py-3 !px-5 !text-[0.78rem]"
            data-testid="header-cta-quote"
          >
            <i className="fa-regular fa-paper-plane" />
            Solicitar Orçamento
          </button>
        </div>

        <button
          type="button"
          aria-label="Abrir menu"
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-[var(--mn-line)] rounded-sm transition-colors duration-300"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <MobileMenu
        open={open}
        items={navItems}
        locationPathname={location.pathname}
        onItemClick={handleMobileNav}
        onCtaClick={() => {
          setOpen(false);
          onQuoteClick?.();
        }}
      />
    </header>
  );
};

export default Header;
